import { Injectable } from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {
  GetAdvancedParams,
  GetParams,
  GetResponse,
  InvoiceOrder,
  InvoiceOrderCreate,
  OrderSendProposal,
  Partner,
  PostResponse,
  PurchaseOrder
} from '@app/shared';
import {map, Observable} from 'rxjs';
// Directo al fichero y no al barril `@app/shared`: core solo puede tomar TIPOS del barril. Un valor
// convierte el ciclo core ↔ shared en real y la app no arranca («Cannot access … before initialization»)
import {normalizeRecipients} from '../../shared/utils/order-send-recipients-utils';
import {convertGetParamsToHttpParams} from '../utils/api-utils';
import {GlobalValuesService} from './global-values.service';
import {CompanyService} from './company.service';

@Injectable({
  providedIn: 'root'
})
export class InvoiceOrderService {
  /** URLs principales relacionadas con InvoiceOrder **/
  InvoiceOrderUrl = environment.financeApiBaseUrl + '/api/invoiceOrder';
  PurchaseOrderUrl = environment.financeApiBaseUrl + '/api/purchaseOrder';
  InvoiceOrderUrlPdf = environment.financeApiBaseUrl + '/api/invoiceOrder/file';
  PurchaseOrderPdfUrl = environment.financeApiBaseUrl + '/api/purchaseOrder/file';
  InvoiceOrderAdvancedForAdminUrl = environment.financeApiBaseUrl + '/api/invoiceOrder/advanced-for-admin';
  PurchaseOrderAdvancedForAdminUrl = environment.financeApiBaseUrl + '/api/purchaseOrder/advanced-for-admin';
  constructor(
    private http: HttpClient,
    private globalValuesService: GlobalValuesService,
    private companyService: CompanyService
  ) { }

  /** Metodos de obtener/crear/actualizar/eliminar InvoiceOrder **/
  getInvoiceOrder(getParams: GetParams, accessToken?: string): Observable<GetResponse> {
    let headers = new HttpHeaders();

    // Si el accessToken es proporcionado, agregamos el header Authorization
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    // Realizamos la petición GET con los parámetros y, si es necesario, con el token de autorización
    return this.http.get<GetResponse>(this.InvoiceOrderUrl, {
      params: convertGetParamsToHttpParams(getParams),
      headers: headers  // Pasamos los headers
    });
  }


  createInvoiceOrder(newInvoiceOrder: InvoiceOrderCreate): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.InvoiceOrderUrl, newInvoiceOrder);
  }

  updateInvoiceOrder(InvoiceOrder: InvoiceOrder): Observable<any> {
    return this.http.put(this.InvoiceOrderUrl, InvoiceOrder);
  }

  patchInvoiceOrder(value: any): Observable<any> {
    return this.http.patch(this.InvoiceOrderUrl, value);
  }

  patchInvoiceOrderExchangeRate(payload: { id: number, exchangeRate: number | null }): Observable<any> {
    return this.http.patch(this.InvoiceOrderUrl, payload);
  }

  patchFactureInvoiceOrder(payload: {
    id: number,
    clientInvoiceId: number | null,
    clientInvoiceDate: string | null,
    clientInvoiceAmount: number | null
  }): Observable<any> {
    return this.http.patch(this.InvoiceOrderUrl + '/client-invoice', payload);
  }

  deleteInvoiceOrder(invoiceOrderIds: number[]): Observable<any> {
    // Crear parámetros de consulta con múltiples valores para 'ids'
    let params = new HttpParams();
    invoiceOrderIds.forEach(id => {
      params = params.append('ids', id.toString());
    });

    return this.http.delete(this.InvoiceOrderUrl, { params });
  }

  /** Metodos de obtener/crear/actualizar/eliminar PurchaseOrder **/
  getPurchaseOrder(getParams: GetParams, accessToken?: string): Observable<GetResponse> {
    let headers = new HttpHeaders();

    // Si el accessToken es proporcionado, agregamos el header Authorization
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    // Realizamos la petición GET con los parámetros y, si es necesario, con el token de autorización
    return this.http.get<GetResponse>(this.PurchaseOrderUrl, {
      params: convertGetParamsToHttpParams(getParams),
      headers: headers  // Pasamos los headers
    });
  }


  createPurchaseOrder(newPurchaseOrder: InvoiceOrderCreate): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.PurchaseOrderUrl, newPurchaseOrder);
  }

  updatePurchaseOrder(PurchaseOrder: PurchaseOrder): Observable<any> {
    return this.http.put(this.PurchaseOrderUrl, PurchaseOrder);
  }

  patchPurchaseOrder(value: any): Observable<any> {
    return this.http.patch(this.PurchaseOrderUrl, value);
  }

  patchPurchaseOrderExchangeRate(payload: { id: number, exchangeRate: number | null }): Observable<any> {
    return this.http.patch(this.PurchaseOrderUrl, payload);
  }

  patchFacturePurchaseOrder(payload: {
    id: number,
    supplierInvoiceId: number | null,
    supplierInvoiceDate: string | null,
    supplierInvoiceAmount: number | null
  }): Observable<any> {
    return this.http.patch(this.PurchaseOrderUrl + '/supplier-invoice', payload);
  }

  deletePurchaseOrder(invoiceOrderIds: number[]): Observable<any> {
    // Crear parámetros de consulta con múltiples valores para 'ids'
    let params = new HttpParams();
    invoiceOrderIds.forEach(id => {
      params = params.append('ids', id.toString());
    });

    return this.http.delete(this.PurchaseOrderUrl, { params });
  }

  downloadInvoiceOrderPdf(orderId: number, partnerId: number, currencyId: number, companyName?: string | null): Observable<Blob> {
    let params = new HttpParams()
      .set('invoiceOrderId', orderId.toString())
      .set('currencyId', currencyId.toString())
      .set('receptorId', partnerId.toString());
    
    if (companyName) {
      params = params.set('companyName', companyName);
    }
    
    return this.http.get(this.InvoiceOrderUrlPdf, {params, responseType: 'blob'});
  }

  downloadPurchaseorderPdf(orderId: number, partnerId: number, currencyId: number, companyName?: string | null): Observable<Blob> {
    let params = new HttpParams()
      .set('purchaseOrderId', orderId.toString())
      .set('currencyId', currencyId.toString())
      .set('receptorId', partnerId.toString());
    
    if (companyName) {
      params = params.set('companyName', companyName);
    }
    
    return this.http.get(this.PurchaseOrderPdfUrl, {params, responseType: 'blob'});
  }


  generatePdf(isInvoiceOrder: boolean, orderId: number, currencyId: number, executorPartnerId: number, partnerId?: number, companyName?: string | null): Observable<any> {
    const payload: any = {
      currencyId,
      executorPartnerId
    };

    if (isInvoiceOrder) {
      payload.invoiceOrderId = orderId;
      if (partnerId) {
        payload.partnerId = partnerId;
      }
    } else {
      payload.purchaseOrderId = orderId;
    }

    if (companyName) {
      payload.companyName = companyName;
    }

    return this.http.post((isInvoiceOrder ? this.InvoiceOrderUrl : this.PurchaseOrderUrl) + '/regenerate-pdf', payload);
  }

  /** Destinatarios que propone finance al enviar un EP/OC; la persona los edita antes de enviar (SOU-544). */
  getSendProposal(isInvoiceOrder: boolean, orderId: number, currencyId: number): Observable<OrderSendProposal> {
    const params = new HttpParams()
      .set(isInvoiceOrder ? 'invoiceOrderId' : 'purchaseOrderId', orderId.toString())
      .set('currencyId', currencyId.toString());

    return this.http.get<any>((isInvoiceOrder ? this.InvoiceOrderUrl : this.PurchaseOrderUrl) + '/send-proposal', {params})
      .pipe(map(rsp => {
        // Los endpoints de finance suelen envolver la respuesta en `data`
        const proposal = rsp?.data ?? rsp;
        return {
          orderId: proposal?.orderId ?? orderId,
          currencyId: proposal?.currencyId ?? currencyId,
          recipients: normalizeRecipients(proposal?.recipients),
        };
      }));
  }

  setFirmRouteData(order: InvoiceOrder | PurchaseOrder) {
    // Primero obtenemos todos los partners potenciales
    const revision = this.getPartner(order.revisionId || 0);
    const shipper = this.getPartner(order.shipperId || 0);
    const transmitter = this.getPartner(order.transmitterId || 0);
    const approver = this.getPartner(order.approverId || 0);
    const rejector = this.getPartner(order.rejectorId || 0);

    // Luego asignamos según el estado
    switch (order.state) {
      case 'Draft':
        order.transmitter = undefined;
        order.transmitterDate = undefined;
        order.revision = undefined;
        order.revisionDate = undefined;
        order.shipper = undefined;
        order.shipperDate = undefined;
        order.approver = undefined;
        order.approverDate = undefined;
        order.rejector = undefined;
        order.rejectedDate = undefined;
        break;

      case 'Issued':
        order.transmitter = transmitter;
        order.revision = undefined;
        order.revisionDate = undefined;
        order.shipper = undefined;
        order.shipperDate = undefined;
        order.approver = undefined;
        order.approverDate = undefined;
        order.rejector = undefined;
        order.rejectedDate = undefined;
        break;

      case 'Reviewed':
      case 'Rejected':
        order.transmitter = transmitter;
        order.revision = revision;
        order.rejector = rejector;
        order.shipper = undefined;
        order.shipperDate = undefined;
        order.approver = undefined;
        order.approverDate = undefined;

        break;

      case 'Sent':
        // SOU-481: con el flujo nuevo "Enviado" va DESPUÉS de "Aprobado", así que una
        // orden en Sent normalmente ya está aprobada y su aprobación debe conservarse.
        // En las órdenes antiguas (enviadas sin aprobar) approverId viene vacío y
        // getPartner lo resuelve a undefined por sí solo, sin necesidad de borrarlo.
        order.transmitter = transmitter;
        order.revision = revision;
        order.shipper = shipper;
        order.rejector = rejector;
        order.approver = approver;
        break;

      case 'Approved':
        // En estado approved mostramos todos
        order.transmitter = transmitter;
        order.revision = revision;
        order.shipper = shipper;
        order.approver = approver;
        order.rejector = rejector;
        break;

      default:
        // Para cualquier otro estado, no asignamos ninguno
        order.transmitter = undefined;
        order.revision = undefined;
        order.shipper = undefined;
        order.approver = undefined;
        order.rejector = undefined;
        order.revisionDate = undefined;
        order.shipperDate = undefined;
        order.approverDate = undefined;
        order.rejectedDate = undefined;

    }
  }

  getPartner(id: number): Partner | undefined {
    return this.globalValuesService.partnerlist.find(p => p.id === id);
  }

  /** Métodos avanzados para Administración **/
  getAdvancedInvoiceOrderForAdmin(getParams: GetAdvancedParams): Observable<GetResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<GetResponse>(this.InvoiceOrderAdvancedForAdminUrl, getParams, {headers});
  }

  getAdvancedPurchaseOrderForAdmin(getParams: GetAdvancedParams): Observable<GetResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<GetResponse>(this.PurchaseOrderAdvancedForAdminUrl, getParams, {headers});
  }

  /** Métodos para obtener proyectos de IO y PO **/
  getInvoiceOrderProjects(): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.get<PostResponse>(this.InvoiceOrderUrl + '/projects', {headers});
  }

  getPurchaseOrderProjects(): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.get<PostResponse>(this.PurchaseOrderUrl + '/projects', {headers});
  }
}
